// config/permissions.js

import { useState, useEffect, useRef, useCallback } from "react";
import { BASE_URL } from "./axios";
import axios from "axios";
import { toast } from "react-toastify";
import { handleAllError } from "../components/CustomFunctions";
import { formatDateTimeDifference } from "../components/Date";

const usePermission = () => {
  const [PERMISSION_HR_ADMIN, setPermissionHRAdmin] = useState(false);
  const [PERMISSION_AUTHORITY, setPermissionAuthority] = useState(false);
  const [PERMISSION_ASSIGNED_ADMIN, setPermissionAssignedAdmin] =
    useState(false);
  const [SUPER_ADMIN, setSuperAdmin] = useState(false);
  const [PERMISSION_RH, setPermissionIsRH] = useState(false);
  const [TICKET_SUPPORT, setTicketSupport] = useState(false);
  const [PERMISSION_PORTAL_SETTINGS_EDIT, setPermissionPortalSettingsEdit] = useState(false);
  const [SUB_COMPANIES, setSubCompanies] = useState([]);
  const [PORTAL_SETTINGS, setPortalSettings] = useState(null);

  // New states for time tamper detection
  const [timeDifferenceLoader, setTimeDifferenceLoader] = useState(false);
  const [timeTampered, setTimeTampered] = useState(false);
  const [timeDiff, setTimeDiff] = useState(0);
  const [serverTime, setServerTime] = useState(null);
  const [lastCheck, setLastCheck] = useState(Date.now());
  // Add these with your other state declarations
  const [showTimeBlocker, setShowTimeBlocker] = useState(false);
  const [blockerData, setBlockerData] = useState(null);
  const [sslError, setSslError] = useState(false);

  const toastShownRef = useRef(false);

  // const development_mode = true; // false for production
  const development_mode = false; // false for production

  const updatePermissions = () => {
    const role = sessionStorage.getItem("role");
    const code = sessionStorage.getItem("emp_code");
    const isRH = sessionStorage.getItem("is_rh");
    setPermissionHRAdmin(role === "HR" || role === "Admin");
    setPermissionAuthority(role === "Admin" || role === "SuperAdmin");
    setPermissionAssignedAdmin(
      code === "CIPL1" ||
        code === "CIPL6" ||
        code === "CIPL16" ||
        code === "CIPL41" ||
        code === "AiM10000" ||
        code === "CIPL10002" ||
        code === "CIPL10007",
    );
    setSuperAdmin(code === "AiM10000");
    setTicketSupport(code === "CIPL10007");
    setPermissionIsRH(isRH);
    setPermissionPortalSettingsEdit(code === "CIPL1" || code === "AiM10000");
  };

  const getSubCompany = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/wfm/subcompanylist/null/active/`,
      );
      setSubCompanies(res.data);
    } catch (err) {
      handleAllError(err);
    }
  };


  const getCompanyId = () => {
  // 1. Normal flow (same tab)
  const sessionCompanyId = sessionStorage.getItem("company_id");
  if (sessionCompanyId) return sessionCompanyId;

  // 2. New tab fallback
  const params = new URLSearchParams(window.location.search);
  return params.get("company_id") || "null";
};


  const getPortalSettings = async () => {
    try {
      const companyId = getCompanyId();
      
      const res = await axios.get(
        // `${BASE_URL}/wfm/portal_setting_sub_company/${sessionStorage.getItem("company_id") || "null"}/`,
         `${BASE_URL}/wfm/portal_setting_sub_company/${companyId}/`,
      );
      setPortalSettings(res.data);
    } catch (err) {
      // handleAllError(err)
      console.error("portal setting by sub_company error:", err);
    }
  };

  const cancelTokenRef = useRef(null);

  // New function to check time tampering
  const checkTimeTampering = async (showToast = false, alreadyHit = false) => {
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Request cancelled due to new request");
      cancelTokenRef.current = null;
    }

    // Reset SSL error state
    setSslError(false);

    try {
      setTimeDifferenceLoader(true);
      // Create new cancel token
      const source = axios.CancelToken.source();
      cancelTokenRef.current = source;

      const token = sessionStorage.getItem("access_token");
      if (!token) {
        setTimeDifferenceLoader(false);
        return;
      }

      const response = await axios
        .get(`${BASE_URL}/wfm/server-time-dynamic/`, {
          headers: {
            "Content-Type": "application/json",
          },
          cancelToken: source.token,
          timeout: 5000,
        })
        .catch((error) => {
          // Check if it's an SSL/certificate error
          if (
            error.code === "ERR_NETWORK" &&
            error.message.includes("certificate")
          ) {
            console.log(
              "🔐 SSL Certificate error detected - system time is likely incorrect",
            );
            setSslError(true);
            setShowTimeBlocker(true);

            // Create mock data to show in blocker
            const mockData = {
              serverTime: Date.now(),
              asia_kolkata: {
                datetime: new Date().toISOString(),
                timezone: "Asia/Kolkata",
              },
            };
            setBlockerData(mockData);

            toast.error(
              `System time tampering detected! Your system time is incorrect (SSL certificate validation failed). Please fix your system time to continue.`,
              {
                position: "top-center",
                hideProgressBar: false,
                autoClose: false,
                closeOnClick: true,
                draggable: true,
              },
            );

            throw error;
          }
          throw error;
        });

      if (response.status === 200) {
        const data = response.data;
        setServerTime(data);

        const diff = Math.abs(data.serverTime - Date.now());
        setTimeDiff(diff);

        // Store expected timezone on first check
        if (!localStorage.getItem("expected_timezone")) {
          const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
          localStorage.setItem("expected_timezone", userTimezone);
        }

        // Check timezone consistency
        const storedTimezone = localStorage.getItem("expected_timezone");
        const currentTimezone =
          Intl.DateTimeFormat().resolvedOptions().timeZone;
        const timezoneChanged =
          storedTimezone && storedTimezone !== currentTimezone;

        // CASE 1: More than 6 hours - BLOCK ACCESS
        if (diff > 21600000) {
          // 6 hours
          console.log(
            "🛑 Blocking access - time difference:",
            formatDateTimeDifference(diff),
          );
          setBlockerData(data);
          setShowTimeBlocker(true);
          setTimeTampered(true);
          console.log("🔴 showTimeBlocker set to:", true); // Debug log

          // Show error toast only once when blocker is shown
          if (!showTimeBlocker) {
            toast.error(
              `System time tampering detected! time is off by ${formatDateTimeDifference(diff)}. Please fix your system time to continue.`,
              {
                position: "top-center",
                hideProgressBar: false,
                autoClose: false,
                closeOnClick: true,
                draggable: true,
              },
            );
          }
          setLastCheck(Date.now());
          return;
        }

        // CASE 2: Between 5 minutes and 6 hours - SHOW TOAST WARNING (only when showToast=true)
        if (diff > 300000 && diff <= 21600000) {
          // 5min to 6 hours
          if (showToast && !toastShownRef.current) {
            toastShownRef.current = true;
            // const minutes = Math.round(diff / 60000);
            // const hours = Math.floor(diff / 3600000);
            // const remainingMinutes = Math.round((diff % 3600000) / 60000);

            // let timeMessage = '';
            // if (hours > 0) {
            //   timeMessage = `${hours} hour${hours > 1 ? 's' : ''} ${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`;
            // } else {
            //   timeMessage = `${minutes} minutes`;
            // }

            toast.warning(
              `⚠️ Your system time is off by ${formatDateTimeDifference(diff)}. Some features may not work correctly.`,
              {
                position: "top-center",
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                onClose: () => {
                  toastShownRef.current = false;
                }, // Reset when toast closes
              },
            );
          }
          setShowTimeBlocker(false);
          setTimeTampered(true);
        }

        // CASE 3: Less than 5 minutes - NO ACTION (do nothing)
        else {
          // Hide blocker if it was shown
          setShowTimeBlocker(false);
          setTimeTampered(false);
        }
        // Hide blocker if it was shown
        setTimeTampered(diff > 300000 || timezoneChanged);
        setLastCheck(Date.now());
      } else {
        console.log("server-time-check-failed");
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Previous request (server time) cancelled:", error.message);
      } else {
        console.error("Time tamper check error:", error);
      }
    } finally {
      setTimeDifferenceLoader(false);
    }
  };

  // useEffect(() => {
  //   return () => {
  //     if (cancelTokenRef.current) {
  //       cancelTokenRef.current.cancel('Component unmounted');
  //     }
  //   };
  // }, []);

  const retryTimeCheck = async () => {
    setShowTimeBlocker(false);
    setSslError(false);
    // Fetch system time again
    await checkTimeTampering(true);
  };

  // NEW: Function to force immediate time check
  const forceImmediateTimeCheck = useCallback(async (showToast = true) => {
    console.log("🔍 Forcing immediate time check...");

    // Cancel any pending request
    if (cancelTokenRef.current) {
      cancelTokenRef.current.cancel("Forcing new check");
      cancelTokenRef.current = null;
    }

    // Clear any existing time difference loader
    setTimeDifferenceLoader(false);

    // Small delay to ensure clean state
    await new Promise((resolve) => setTimeout(resolve, 100));

    // Perform the check
    await checkTimeTampering(showToast);
  }, []);

  // Add time check before every API call (interceptor)
  useEffect(() => {
    // Setup axios interceptor to check time before each request
    const interceptor = axios.interceptors.request.use(
      async (config) => {
        // Skip time check for server-time endpoint itself
        if (config.url.includes("/server-time-dynamic/")) {
          return config;
        }

        // Don't make API calls if blocker is already shown
        if (showTimeBlocker) {
          return Promise.reject({ message: "Time tampering detected" });
        }

        // Check if last check was more than 5 minutes ago
        if (Date.now() - lastCheck > 300000) {
          // 5 minutes
          await checkTimeTampering(true); // Show toast if needed
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, [lastCheck, showTimeBlocker]); // Only re-run when lastCheck changes

  useEffect(() => {
    updatePermissions();
    getSubCompany();
    getPortalSettings();

    // Initial time check
    if (!development_mode) {
      checkTimeTampering();
    }
    // Listen for tab visibility changes (when user switches tabs)
    const handleVisibilityChangeBackup = () => {
      if (document.visibilityState === "visible") {
        // User came back to this tab, check time immediately
        checkTimeTampering(true);
      }
    };

    // Listen for tab visibility changes - IMMEDIATE CHECK
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        console.log("👁️ Tab became visible - checking time immediately");
        // User came back to this tab, check time immediately
        if (!development_mode) {
          checkTimeTampering(true);
        }
      }
    };

    // const handleStorageChange = () => {
    //   updatePermissions();
    // };

    // Listen for storage events (if time changed in another tab)
    const handleStorageChange = (e) => {
      if (e.key === "time_override" || e.key === "expected_timezone") {
        updatePermissions();
      }
      // Also check time when storage changes
      if (!development_mode) {
        checkTimeTampering(true);
      }
    };

    // Listen for system time changes (if possible)
    const handleTimeChangeBackup = () => {
      // This is a crude way to detect time changes
      const now = Date.now();
      if (Math.abs(now - lastCheck) > 60000) {
        // If time jumped by more than a minute
        if (!development_mode) {
          checkTimeTampering(true);
        }
      }
    };

    // Listen for system time changes - IMMEDIATE DETECTION
    const handleTimeChange = () => {
      console.log("🕒 Window focused - checking time immediately");
      // Always check immediately when window gets focus
      if (!development_mode) {
        checkTimeTampering(true);
      }
    };

    // NEW: Monitor for system time changes using performance.now()
    let lastPerformanceNow = performance.now();
    let lastDateNow = Date.now();

    const timeJumpMonitor = setInterval(() => {
      const currentPerfNow = performance.now();
      const currentDateNow = Date.now();

      // Calculate expected time passage
      const perfElapsed = currentPerfNow - lastPerformanceNow;
      const dateElapsed = currentDateNow - lastDateNow;

      // If there's a significant discrepancy, system time might have been changed
      if (Math.abs(dateElapsed - perfElapsed) > 2000) {
        // 2 second threshold
        console.log("⚠️ Possible system time jump detected!", {
          expected: perfElapsed,
          actual: dateElapsed,
          difference: dateElapsed - perfElapsed,
        });

        // Force immediate time check
        if (!development_mode) {
          checkTimeTampering(true);
        }
      }

      lastPerformanceNow = currentPerfNow;
      lastDateNow = currentDateNow;
    }, 1000); // Check every second

    // 5-minute fallback interval
    const timeCheckInterval = setInterval(() => {
      if (!development_mode) {
        checkTimeTampering(true);
      }
    }, 300000); // Every 5 minute

    window.addEventListener("storage", handleStorageChange);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleTimeChange); // Check when window gets focus

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleTimeChange);
      if (cancelTokenRef.current) {
        cancelTokenRef.current.cancel("Component unmounted");
      }
      clearInterval(timeCheckInterval);
      clearInterval(timeJumpMonitor);
    };
  }, []);

  return {
    PERMISSION_HR_ADMIN,
    PERMISSION_AUTHORITY,
    PERMISSION_ASSIGNED_ADMIN,
    SUPER_ADMIN,
    TICKET_SUPPORT,
    PERMISSION_RH,
    PERMISSION_PORTAL_SETTINGS_EDIT,
    updatePermissions,
    SUB_COMPANIES,
    PORTAL_SETTINGS,

    // Time Tamper related exports
    timeTampered,
    timeDiff,
    timeDifferenceLoader,
    serverTime,
    checkTimeTampering,
    showTimeBlocker,
    blockerData,
    retryTimeCheck,
    sslError,
    forceImmediateTimeCheck,
  };
};

export default usePermission;
